/*
 * 模块: 周期概览组件
 * 职责: 显示年度周期网格视图，支持年份切换和周期选择
 * 依赖: React, Ant Design, 类型定义, 工具函数
 * 导出: PeriodOverview 组件
 * 限制: 文件<280行, 函数<30行, 组件职责单一
 */

import React from 'react';
import { Button, Space, Tooltip, Popconfirm, Typography } from 'antd';
import {
  CheckCircleOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import type { Period } from '../../../types';
import type { WeekOverviewItem } from '../types';
import {
  getTotalWeeksInYear,
  getWeekDateRange,
  getAvailableYears
} from '../utils/dateUtils';

const { Text } = Typography;

interface PeriodOverviewProps {
  periods: Period[];
  selectedPeriodId?: number;
  selectedYear: number;
  onYearChange: (year: number) => void;
  onPeriodSelect: (periodId: number) => void;
  onUploadClick: () => void;
  onDelete: (periodId: number) => void;
}

/**
 * 生成指定年份的全部周期概览
 */
const generateYearOverview = (
  year: number,
  periods: Period[]
): WeekOverviewItem[] => {
  const weeks: WeekOverviewItem[] = [];
  const totalWeeks = getTotalWeeksInYear(year);

  for (let weekNum = 1; weekNum <= totalWeeks; weekNum++) {
    const weekLabel = `${year}-W${weekNum.toString().padStart(2, '0')}`;
    const period = periods.find(p => p.week_number === weekLabel);
    const uploaded = !!period;

    const { startDate, endDate, weekDisplayLabel } = getWeekDateRange(year, weekNum);

    weeks.push({
      weekNum,
      weekLabel,
      weekDisplayLabel,
      uploaded,
      period,
      startDate,
      endDate
    });
  }

  return weeks;
};

/**
 * 周期概览组件
 */
export const PeriodOverview: React.FC<PeriodOverviewProps> = ({
  periods,
  selectedPeriodId,
  selectedYear,
  onYearChange,
  onPeriodSelect,
  onUploadClick,
  onDelete
}) => {
  const weekOverview = generateYearOverview(selectedYear, periods);
  const availableYears = getAvailableYears(periods);
  const uploadedCount = weekOverview.filter(w => w.uploaded).length;

  return (
    <div className="period-overview-panel">
      {/* 头部 */}
      <div className="overview-header">
        <div className="overview-header-left">
          <span className="overview-title">
            📊 {selectedYear}年周期数据概览
          </span>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
            (共 {weekOverview.length} 周，已上传 {uploadedCount} 周)
          </Text>
        </div>
        <div className="overview-header-right">
          <Space size="middle">
            {/* 图例 */}
            <div className="overview-legend">
              <span className="legend-item">
                <span className="legend-dot uploaded"></span>
                已上传
              </span>
              <span className="legend-item">
                <span className="legend-dot not-uploaded"></span>
                未上传
              </span>
            </div>
            {/* 年份选择器 */}
            <div className="year-selector">
              <Button
                size="small"
                disabled={selectedYear <= Math.min(...availableYears)}
                onClick={() => onYearChange(selectedYear - 1)}
              >
                ◀
              </Button>
              <span className="current-year">{selectedYear}</span>
              <Button
                size="small"
                disabled={selectedYear >= Math.max(...availableYears)}
                onClick={() => onYearChange(selectedYear + 1)}
              >
                ▶
              </Button>
            </div>
          </Space>
        </div>
      </div>

      {/* 周期网格 */}
      <div className="overview-grid">
        {weekOverview.map(week => (
          <WeekCell
            key={week.weekLabel}
            week={week}
            isSelected={week.period?.id === selectedPeriodId}
            onSelect={onPeriodSelect}
            onUploadClick={onUploadClick}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * 单个周期单元格组件
 */
interface WeekCellProps {
  week: WeekOverviewItem;
  isSelected: boolean;
  onSelect: (periodId: number) => void;
  onUploadClick: () => void;
  onDelete: (periodId: number) => void;
}

const WeekCell: React.FC<WeekCellProps> = ({
  week,
  isSelected,
  onSelect,
  onUploadClick,
  onDelete
}) => {
  const handleClick = () => {
    if (week.uploaded && week.period) {
      onSelect(week.period.id);
    } else {
      onUploadClick();
    }
  };

  return (
    <Tooltip
      title={
        <div>
          <div><strong>{week.weekDisplayLabel}</strong></div>
          <div>日期范围：{week.startDate} 至 {week.endDate}</div>
          <div style={{ marginTop: 4, fontSize: 11, opacity: 0.8 }}>
            ISO周：{week.weekLabel}
          </div>
          {week.uploaded && (
            <div style={{ marginTop: 4 }}>
              ✓ 已上传 ({week.period?.keywords_count || 0} 个关键词)
            </div>
          )}
          {!week.uploaded && (
            <div style={{ marginTop: 4 }}>点击上传数据</div>
          )}
        </div>
      }
    >
      <div
        className={`overview-week-cell ${week.uploaded ? 'uploaded' : 'not-uploaded'} ${
          isSelected ? 'selected' : ''
        }`}
      >
        <div className="week-cell-content" onClick={handleClick}>
          <div className="week-number">{week.weekDisplayLabel}</div>
          <div className="week-status">
            {week.uploaded ? (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />
            ) : (
              <PlusOutlined style={{ color: '#d9d9d9', fontSize: 10 }} />
            )}
          </div>
        </div>
        {week.uploaded && week.period && (
          <Popconfirm
            title="确定要删除这个周期的数据吗？"
            description="此操作将永久删除该周期的所有数据，包括关键词指标、趋势分析等。"
            onConfirm={(e) => {
              e?.stopPropagation();
              onDelete(week.period!.id);
            }}
            okText="确定删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <DeleteOutlined
              className="week-delete-btn"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        )}
      </div>
    </Tooltip>
  );
};

export default PeriodOverview;

